


import React, { useState, useEffect } from 'react';
import { GripVertical, Edit2, Save, X, PlusCircle, Trash2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axiosInstance from '../utils/axiosInstance';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import LoadingSpinner from '../components/LoadingSpinner';

const LoanTermsManager = () => {
  const [loanTerms, setLoanTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [bulkTerms, setBulkTerms] = useState([{
    tenure: '',
    lowerAmount: '',
    higherAmount: '',
    interestRate: '',
    isPayday: false,
    isNew: true
  }]);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    fetchLoanTerms();
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    const value = parseFloat(amount).toFixed(2);
    return `₹${parseInt(value).toLocaleString('en-IN')}`;
  };

  const fetchLoanTerms = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/loan-terms');
      setLoanTerms(response.data.data);
    } catch (error) {
      console.error('Error fetching loan terms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (term) => {
    setEditingId(term.id);
    setEditForm({
      tenure: term.tenure,
      lowerAmount: term.lowerAmount,
      higherAmount: term.higherAmount,
      interestRate: term.interestRate,
      isPayday: term.isPayday,
      sortOrder: term.sortOrder
    });
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSave = async () => {
    try {
      const updatedTerm = {
        id: editingId,
        ...editForm,
        tenure: parseInt(editForm.tenure),
        lowerAmount: parseFloat(editForm.lowerAmount),
        higherAmount: parseFloat(editForm.higherAmount),
        interestRate: parseFloat(editForm.interestRate)
      };
      setLoading(true);
      await axiosInstance.post('/loan-terms/upsert', updatedTerm);
      setEditingId(null);
      setEditForm({});
      fetchLoanTerms();
    } catch (error) {
      console.error('Error updating term:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(loanTerms);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      sortOrder: index + 1
    }));

    setLoanTerms(updatedItems);

    try {
      const sortUpdateData = updatedItems.map(({ id, sortOrder }) => ({
        id,
        sortOrder
      }));
      
      await axiosInstance.post('/loan-terms/upsert', updatedItems);
    } catch (error) {
      console.error('Error updating sort order:', error);
      fetchLoanTerms();
    }
  };
  const handleAddNewRow = () => {
    setBulkTerms([...bulkTerms, {
      tenure: '',
      lowerAmount: '',
      higherAmount: '',
      interestRate: '',
      isPayday: false,
      isNew: true
    }]);
  };
  
  const handleRemoveRow = (index) => {
    const updatedTerms = bulkTerms.filter((_, idx) => idx !== index);
    setBulkTerms(updatedTerms);
  };
  
  const handleBulkTermChange = (index, field, value) => {
    const updatedTerms = [...bulkTerms];
    updatedTerms[index] = {
      ...updatedTerms[index],
      [field]: value
    };
    setBulkTerms(updatedTerms);
  };
  
  const handleSaveBulkTerms = async () => {
    try {
      const termsToSave = bulkTerms
        .filter(term => term.tenure && term.lowerAmount && term.higherAmount && term.interestRate)
        .map(term => ({
          ...term,
          tenure: parseInt(term.tenure),
          lowerAmount: parseFloat(term.lowerAmount),
          higherAmount: parseFloat(term.higherAmount),
          interestRate: parseFloat(term.interestRate),
          isPayday: Boolean(term.isPayday)
        }));
  
      if (termsToSave.length === 0) {
        console.error('No valid terms to save');
        return;
      }
  setLoading(true)
      const response = await axiosInstance.post('/loan-terms/upsert', termsToSave);
      
      if (response.data) {
        setBulkTerms([{
          tenure: '',
          lowerAmount: '',
          higherAmount: '',
          interestRate: '',
          isPayday: false,
          isNew: true
        }]);
        setIsAdding(false);
        fetchLoanTerms();
      }
    } catch (error) {
      console.error('Error adding loan terms:', error);
    }
    finally{
        setLoading(false)
  
    }
  };
  const handleDeleteTerm = (termId) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className='p-6 bg-white rounded-lg shadow-lg max-w-sm'>
            <h1 className="text-xl font-semibold mb-4">Confirm Delete</h1>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this loan term? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                onClick={async () => {
                  try {
                    await axiosInstance.delete(`/loan-terms/loan-terms/${termId}`);
                    fetchLoanTerms();
                    onClose();
                  } catch (error) {
                    console.error('Error deleting term:', error);
                    alert('Failed to delete loan term. Please try again.');
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        );
      }
    });
  };

  const renderTermContent = (term) => {
    if (editingId === term.id) {
      return (
        <div className="grid grid-cols-[2fr_2fr_2fr_2fr_1fr] gap-4 w-full items-center">
          <input
            type="number"
            value={editForm.tenure}
            onChange={(e) => handleEditChange('tenure', e.target.value)}
            className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Tenure"
          />
          <input
            type="number"
            value={editForm.lowerAmount}
            onChange={(e) => handleEditChange('lowerAmount', e.target.value)}
            className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Min Amount"
          />
          <input
            type="number"
            value={editForm.higherAmount}
            onChange={(e) => handleEditChange('higherAmount', e.target.value)}
            className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Max Amount"
          />
          <input
            type="number"
            value={editForm.interestRate}
            onChange={(e) => handleEditChange('interestRate', e.target.value)}
            className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Interest Rate"
          />
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={editForm.isPayday}
              onChange={(e) => handleEditChange('isPayday', e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-gray-700">Payday</span>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-[2fr_2fr_2fr_2fr_1fr] gap-4 w-full items-center">
        <div className="text-gray-700">{term.tenure} months</div>
        <div className="text-gray-700">{formatCurrency(term.lowerAmount)}</div>
        <div className="text-gray-700">{formatCurrency(term.higherAmount)}</div>
        <div className="text-gray-700">{term.interestRate}%</div>
        <div className="text-gray-700">{term.isPayday ? 'Payday' : 'Regular'}</div>
      </div>
    );
  };

  return (
    <div className="w-full mx-auto bg-white rounded-lg shadow-md">
      <div className="p-6 border-b">
        <h2 className="text-2xl font-bold">Loan Terms Management</h2>
      </div>
      {loading && <LoadingSpinner />}
      <div className="p-6">
      <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-[2fr_2fr_2fr_2fr_1fr_auto] gap-4 mb-4 font-semibold text-gray-700 px-4 pb-2 border-b">
            <div>Tenure</div>
            <div>Minimum Amt</div>
            <div>Maximum Amt</div>
            <div>Interest Rate</div>
            <div>Type</div>
            <div>Actions</div>
          </div>
          <Droppable droppableId="droppable">  
    {(provided) => (
      <div
        {...provided.droppableProps}
        ref={provided.innerRef}
        className="space-y-2"
      >
        {loanTerms.map((term, index) => (
         <Draggable
         key={term.id}
         draggableId={`${term.id}`}
         index={index}
          >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="border rounded-lg p-4 bg-white shadow-sm flex items-center"
                      >
                        <div {...provided.dragHandleProps} className="mr-4 cursor-move">
                          <GripVertical className="text-gray-400" />
                        </div>
                        <div className="flex-grow">
                          {renderTermContent(term)}
                        </div>
                        <div className="flex items-center ml-4">
                          {editingId === term.id ? (
                            <>
                              <button
                                onClick={handleEditSave}
                                className="p-2 text-green-600 hover:text-green-700"
                              >
                                <Save className="h-5 w-5" />
                              </button>
                              <button
                                onClick={handleEditCancel}
                                className="p-2 text-gray-600 hover:text-gray-700"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditStart(term)}
                                className="p-2 text-blue-600 hover:text-blue-700"
                              >
                                <Edit2 className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteTerm(term.id)}
                                className="p-2 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="mb-4 mt-8 flex items-center text-blue-600 hover:text-blue-700"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          {isAdding ? 'Cancel' : 'Add Terms'}
        </button>
        
        {isAdding && (
          <div className="mb-4 space-y-4">
            {bulkTerms.map((term, index) => (
              <div key={index} className="grid grid-cols-[2fr_2fr_2fr_2fr_1fr_auto] gap-4 bg-gray-50 p-4 rounded-lg items-center">
                <input
                  type="number"
                  value={term.tenure}
                  onChange={(e) => handleBulkTermChange(index, 'tenure', e.target.value)}
                  className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tenure"
                />
                <input
                  type="number"
                  value={term.lowerAmount}
                  onChange={(e) => handleBulkTermChange(index, 'lowerAmount', e.target.value)}
                  className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Min Amount"
                />
                <input
                  type="number"
                  value={term.higherAmount}
                  onChange={(e) => handleBulkTermChange(index, 'higherAmount', e.target.value)}
                  className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Max Amount"
                />
                <input
                  type="number"
                  value={term.interestRate}
                  onChange={(e) => handleBulkTermChange(index, 'interestRate', e.target.value)}
                  className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Interest Rate"
                />
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={term.isPayday}
                    onChange={(e) => handleBulkTermChange(index, 'isPayday', e.target.checked)}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-gray-700">Payday</span>
                </div>
                <button
                  onClick={() => handleRemoveRow(index)}
                  className="p-2 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
            <div className="flex justify-between mt-4">
              <button
                onClick={handleAddNewRow}
                className="p-2 text-blue-600 hover:text-blue-700 flex items-center"
              >
                <PlusCircle className="h-5 w-5 mr-2" />
                Add Another Row
              </button>
              <button
                onClick={handleSaveBulkTerms}
                className="p-2 px-4 text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Save All Terms
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanTermsManager;